import api from './api';

// Piso de resolución que usa el OCR en el backend (ImageProcessingService.OCR_MAX_DIMENSION).
// Comprimir por ENCIMA de este valor no afecta la detección de dorsales, solo aligera la subida.
const MAX_DIMENSION_SUBIDA = 3200;
const CALIDAD_SUBIDA = 0.9;
// Fotos ya livianas no vale la pena re-codificarlas (ahorra CPU del navegador)
const UMBRAL_OMITIR_COMPRESION_BYTES = 1.5 * 1024 * 1024;

// Redimensiona/comprime en el navegador antes de subir. Si algo falla, se sube
// el archivo original sin modificar (nunca debe bloquear la subida).
const comprimirParaSubida = async (file) => {
  if (!file.type.startsWith('image/') || file.size <= UMBRAL_OMITIR_COMPRESION_BYTES) {
    return file;
  }

  try {
    const bitmap = await createImageBitmap(file);
    const { width, height } = bitmap;
    const ladoMayor = Math.max(width, height);

    if (ladoMayor <= MAX_DIMENSION_SUBIDA) {
      bitmap.close();
      return file;
    }

    const escala = MAX_DIMENSION_SUBIDA / ladoMayor;
    const nuevoAncho = Math.round(width * escala);
    const nuevoAlto = Math.round(height * escala);

    const canvas = document.createElement('canvas');
    canvas.width = nuevoAncho;
    canvas.height = nuevoAlto;
    canvas.getContext('2d').drawImage(bitmap, 0, 0, nuevoAncho, nuevoAlto);
    bitmap.close();

    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', CALIDAD_SUBIDA));
    if (!blob) return file;

    const nuevoNombre = file.name.replace(/\.[^.]+$/, '') + '.jpg';
    return new File([blob], nuevoNombre, { type: 'image/jpeg' });
  } catch (err) {
    console.warn('No se pudo comprimir antes de subir, se sube el original:', file.name, err);
    return file;
  }
};

// XMLHttpRequest (no fetch) porque es la única forma de medir el avance de una subida.
// No se usa la instancia `api`: evita enviar el JWT a Azure Storage y que choque con la firma SAS.
const subirBlobConProgreso = (urlInfo, archivo, onFraccion) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', urlInfo.uploadUrl);
    xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
    xhr.setRequestHeader('Content-Type', archivo.type || 'application/octet-stream');
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && e.total > 0) onFraccion(e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Error subiendo ${urlInfo.nombreArchivo} a Blob Storage (HTTP ${xhr.status})`));
      }
    };
    xhr.onerror = () => reject(new Error(`Error de red subiendo ${urlInfo.nombreArchivo}`));
    xhr.send(archivo);
  });

const fotoService = {
  getByEvento: async (eventoId) => {
    const response = await api.get(`/fotos/evento/${eventoId}`);
    return response.data;
  },

  getByCorredor: async (numeroCorredor) => {
    const response = await api.get(`/fotos/corredor/${numeroCorredor}`);
    return response.data;
  },

  uploadFotos: async (eventoId, files, dorsales = [], onProgress) => {
    const BATCH_SIZE = 8;
    // El navegador sube los bytes DIRECTO a Azure Blob Storage (URL con SAS
    // temporal generada por el backend) en vez de mandarlos al backend para
    // que este los reenvíe — elimina el doble salto de red que limitaba la
    // velocidad de subidas masivas a lo que el backend podía procesar.
    const CONCURRENCIA = 6;
    const MAX_REINTENTOS = 3;
    const ESPERA_REINTENTO_MS = 3000;
    const resultados = [];
    const lotesFallidos = [];
    const total = files.length;

    const lotes = [];
    for (let i = 0; i < total; i += BATCH_SIZE) {
      lotes.push({
        inicio: i,
        archivos: files.slice(i, i + BATCH_SIZE),
        dorsales: dorsales.slice(i, i + BATCH_SIZE)
      });
    }

    // El avance se mide en bytes (tamaño original) para que la barra y el tiempo
    // estimado se muevan de forma continua, no solo al terminar cada lote.
    const tamanos = files.map(f => f.size);
    const bytesTotal = tamanos.reduce((s, n) => s + n, 0) || 1;
    const fraccionArchivo = new Array(total).fill(0);
    let bytesSubidos = 0;
    let completados = 0;
    let fotosFallidas = 0;
    let ultimoReporte = 0;

    const reportar = (forzar = false) => {
      if (!onProgress) return;
      const ahora = Date.now();
      if (!forzar && ahora - ultimoReporte < 150) return;
      ultimoReporte = ahora;
      onProgress(Math.min(completados, total), total, fotosFallidas, {
        bytesSubidos: Math.max(0, Math.min(bytesSubidos, bytesTotal)),
        bytesTotal
      });
    };

    const marcarFraccion = (indice, fraccion) => {
      bytesSubidos += (fraccion - fraccionArchivo[indice]) * tamanos[indice];
      fraccionArchivo[indice] = fraccion;
      reportar();
    };

    const subirLote = async (lote) => {
      let intento = 0;
      let exito = false;
      let ultimoError = null;

      // Se comprime una sola vez fuera del loop de reintentos (evita recomprimir en cada retry)
      const archivosParaSubir = await Promise.all(lote.archivos.map(comprimirParaSubida));

      // Reintentar el lote ante fallos de red/timeout en vez de abortar todo el proceso
      while (intento < MAX_REINTENTOS && !exito) {
        intento++;
        // Un reintento vuelve a subir el lote completo: el avance de sus archivos empieza de cero
        lote.archivos.forEach((_, idx) => marcarFraccion(lote.inicio + idx, 0));
        try {
          // 1. Pedir al backend URLs de subida directa (con SAS) para este lote
          const nombresArchivos = archivosParaSubir.map(f => f.name);
          const { data: urls } = await api.post(`/fotos/upload-urls/${eventoId}`, nombresArchivos);

          // 2. Subir cada archivo DIRECTO a Blob Storage (sin pasar por el backend),
          // reportando el avance de cada uno para la barra de progreso.
          await Promise.all(urls.map((urlInfo, idx) =>
            subirBlobConProgreso(urlInfo, archivosParaSubir[idx],
              (fraccion) => marcarFraccion(lote.inicio + idx, fraccion))
          ));

          // 3. Confirmar con el backend: crea el registro Foto y dispara el
          // procesamiento (OCR + marca de agua) en segundo plano
          const confirmaciones = urls.map((urlInfo, idx) => ({
            blobName: urlInfo.blobName,
            blobUrl: urlInfo.blobUrl,
            nombreArchivo: urlInfo.nombreArchivo,
            tamañoBytes: archivosParaSubir[idx].size,
            dorsalManual: lote.dorsales[idx] || ''
          }));
          const response = await api.post(`/fotos/confirmar-subida/${eventoId}`, confirmaciones);

          resultados.push(response.data);
          exito = true;
        } catch (err) {
          ultimoError = err;
          if (intento < MAX_REINTENTOS) {
            await new Promise(resolve => setTimeout(resolve, ESPERA_REINTENTO_MS));
          }
        }
      }

      if (!exito) {
        fotosFallidas += lote.archivos.length;
        lotesFallidos.push({
          archivos: lote.archivos.map(f => f.name),
          error: ultimoError?.response?.data?.mensaje || ultimoError?.message || 'Error desconocido'
        });
      }

      lote.archivos.forEach((_, idx) => marcarFraccion(lote.inicio + idx, 1));
      completados += lote.archivos.length;
      reportar(true);
    };

    // Pool de workers: cada uno toma el siguiente lote disponible hasta agotarlos
    let siguienteIndice = 0;
    const worker = async () => {
      while (siguienteIndice < lotes.length) {
        const lote = lotes[siguienteIndice++];
        await subirLote(lote);
      }
    };

    const workers = Array.from({ length: Math.min(CONCURRENCIA, lotes.length) }, () => worker());
    await Promise.all(workers);

    return { resultados, lotesFallidos };
  },

  cerrarEvento: async (eventoId) => {
    const response = await api.post(`/fotos/evento/${eventoId}/cerrar`);
    return response.data;
  }
};

export default fotoService;
